import { useMemo } from "react";
import { useSelectPlannedWorkShiftsByDate } from "./useSelectPlannedWorkShifts";
import { useSelectPaidWorkHoursAmountByWorkShiftIdAndDate } from "./useSelectWorkHours";
import { useSelectWorkShiftsByDate } from "./useSelectWorkShifts";
import { Base } from "../../framework/common/base";

export type ShiftEntryType = "planned" | "actual";

interface ShiftEntry {
    startsBefore?: boolean;
    endsAfter?: boolean;
    startSlot: number;
    endSlot: number;
    duration: number;
    type: ShiftEntryType;
    routeName?: string;
    startTime: string;
}

type WeekRows = ShiftEntry[][];

export interface WeekRowsByDate {
    [date: string]: WeekRows;
}

interface Shift {
    startTime: string;
    endTime: string;
    duration: number;
    routeName?: string;
}

// Get weekrows from shifts (planned or actual)
const getWeekRows = (
    date: string,
    shifts: Shift[],
    type: ShiftEntryType,
    weekRows: WeekRows
) => {
    shifts
        .filter(
            (s) =>
                s.duration &&
                Base.isOverlapping(
                    s.startTime,
                    s.endTime,
                    date,
                    Base.dateAddDays(date, 7)
                )
        )
        .forEach((s) => {
            const startDate = Base.dateToDateJSON(
                Base.maxDate([date, Base.dateToDateJSON(s.startTime)])
            );
            const endDate = Base.dateToDateJSON(
                Base.minDate([
                    Base.dateAddDays(date, 6),
                    Base.dateToDateJSON(s.endTime),
                ])
            );
            const diff = Base.dateDiffInDays(startDate, endDate);
            const startDiff = Base.dateDiffInDays(date, startDate);

            const startSlot =
                startDiff < 0 ? 0 : startDiff * 2 + (diff > 0 ? 1 : 0);
            const endSlot = diff > 1 ? diff * 2 + startSlot : startSlot + 1;

            const entry: ShiftEntry = {
                startsBefore: Base.isAfter(date, s.startTime, "day"),
                endsAfter: Base.isAfter(s.endTime, endDate, "day"),
                startSlot,
                endSlot,
                duration: s.duration,
                type,
                routeName: s.routeName,
                startTime: s.startTime,
            };

            const slots: number[] = [];
            for (let i = startSlot; i <= endSlot; i++) {
                slots.push(i);
            }

            let entryAdded = false;

            weekRows.forEach((row) => {
                if (!entryAdded && slots.every((s) => !row[s])) {
                    slots.forEach((s) => (row[s] = entry));
                    entryAdded = true;
                }
            });

            if (!entryAdded) {
                const weekRow: ShiftEntry[] = [];
                slots.forEach((s) => (weekRow[s] = entry));
                weekRows.push(weekRow);
            }
        });

    return weekRows;
};

export const useSelectCalendarWeekRows = (
    startDate: string,
    endDate: string
) => {
    const plannedShifts = useSelectPlannedWorkShiftsByDate(startDate, endDate);
    const workShifts = useSelectWorkShiftsByDate(startDate, endDate);
    const workHours = useSelectPaidWorkHoursAmountByWorkShiftIdAndDate(
        startDate,
        endDate
    );

    return useMemo(() => {
        const weekRowsByDate: WeekRowsByDate = {};

        for (
            let date = startDate;
            date <= endDate;
            date = Base.dateToDateJSON(Base.dateAddDays(date, 7))
        ) {
            // Planned shifts can be used as is.
            let weekRows = getWeekRows(date, plannedShifts, "planned", []);
            // Map workShifts to get duration
            weekRows = getWeekRows(
                date,
                workShifts.map(
                    ({ startTime, endTime, workShiftId, effectiveDate }) => ({
                        startTime,
                        endTime: endTime ?? Base.dateToJsonDateTime(new Date()),
                        duration: workHours[`${workShiftId}_${effectiveDate}`],
                    }),
                ),
                "actual",
                weekRows,
            );

            // Map workhours without a shift to show them also.
            weekRows = getWeekRows(
                date,
                Object.keys(workHours)
                    .filter((k) => k.includes("null_")) // Only hours without a shift
                    .map((k) => {
                        const dateStr = k.split("_")[1];

                        return {
                            startTime: dateStr,
                            endTime: dateStr,
                            duration: workHours[k],
                        };
                    }),
                "actual",
                weekRows
            );

            weekRowsByDate[date] = weekRows;
        }

        return weekRowsByDate;
    }, [plannedShifts, workShifts, startDate, endDate, workHours]);
};
